import {
  CapitalChangeAnyPriceI,
  CapitalChangeLimitsI,
  CapitalChangeQuotePriceI,
  CapitalChangeResultI,
} from "../api/request/Lead/Interfaces/CapitalChangeInterface";
import { patchOrganicOffer } from "./organicOffer";

/**
 * «Modifica capitale» del Portal Cliente (04-nov-2026, contrato v1): rejilla de
 * capitales, errores del gateway y textos en italiano de cada bloqueo.
 *
 * Los errores llegan por HTTPService (parseError): `code` es el `error` del cuerpo
 * cuando trae `blockers` o `reason`; si no, el código queda en `message`.
 */

/** Rejilla de AGL (`agl_base_prices` v2) mientras no llegue la de ms-agl. */
export const DEFAULT_CAPITAL_LIMITS: CapitalChangeLimitsI = {
  min: 25000,
  max: 350000,
  step: 5000,
};

/**
 * Euros enteros con separador de miles siempre: Intl en it-IT no agrupa las cifras
 * de 4 dígitos («5000 €»), y el paso de la rejilla se lee mejor como «5.000 €».
 */
export const wholeEuros = (amount: number): string => {
  const sign = amount < 0 ? "-" : "";
  const digits = String(Math.round(Math.abs(amount))).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${digits}\u00a0€`;
};

/** Entero, dentro de los límites y múltiplo del paso. */
export const isCapitalOnGrid = (capital: number, limits: CapitalChangeLimitsI): boolean =>
  Number.isInteger(capital) &&
  capital >= limits.min &&
  capital <= limits.max &&
  0 === capital % limits.step;

/** El capital de la rejilla más cercano (dentro de los límites). */
export const snapCapital = (capital: number, limits: CapitalChangeLimitsI): number => {
  if (!Number.isFinite(capital)) return limits.min;
  const clamped = Math.min(limits.max, Math.max(limits.min, capital));
  const snapped = Math.round(clamped / limits.step) * limits.step;
  return Math.min(limits.max, Math.max(limits.min, snapped));
};

/**
 * Precio de la etapa QUOTE (v1.1): el preventivo aún no tiene oferta elegida, así
 * que no hay periodicidad. Se mira el propio precio y no solo `stage`, para no
 * pintar una cuota que no existe aunque falte `stage`.
 */
export const isQuoteStagePrice = (
  price: CapitalChangeAnyPriceI
): price is CapitalChangeQuotePriceI => null === price.frequency;

/** Etapa del cambio: QUOTE si lo dice ms-agl o si el precio vigente no tiene periodicidad. */
export const isQuoteStageResult = (result: CapitalChangeResultI): boolean =>
  "QUOTE" === result.stage || (null !== result.current && isQuoteStagePrice(result.current));

/**
 * Tras cambiar el capital del preventivo de la sesión, el funnel organic tiene que
 * retomar con el capital nuevo: la pantalla de la oferta re-prepara las ofertas
 * centradas en `capital` de la oferta guardada en sessionStorage
 * (`l5_native_organic_offer`) y preselecciona ese capital; con el viejo, el cliente
 * volvería a ver (y elegir) el capital de antes. Vale en las dos etapas: en OFFER
 * sin tarjeta el cliente aún vuelve a la pantalla de la oferta.
 *
 * Sin oferta guardada (sesión abierta por login) no hay nada que tocar: el gateway
 * centra en el capital del preventivo, que ya es el nuevo. Otro preventivo no se
 * toca: la oferta guardada es la del de la sesión.
 */
export const rememberChangedCapital = (
  sessionLeadUlid: string | null | undefined,
  targetLeadUlid: string,
  result: CapitalChangeResultI
): void => {
  const session = (sessionLeadUlid ?? "").toUpperCase();
  if (!result.changed || "" === session || session !== targetLeadUlid.toUpperCase()) return;
  patchOrganicOffer({ capital: result.to.capital });
};

export type CapitalChangeErrorT =
  | { kind: "REFUSED"; blockers: string[] }
  | { kind: "INVALID_CAPITAL"; reason: string | null }
  | { kind: "NOT_INSURABLE"; reason: string | null }
  | { kind: "NOT_ENABLED" }
  | { kind: "UNKNOWN" };

/** Clasifica el error de la vista previa o del cambio de capital. */
export const capitalChangeError = (error: unknown): CapitalChangeErrorT => {
  const { status, code, message, blockers, reason } = (error ?? {}) as {
    status?: unknown;
    code?: unknown;
    message?: unknown;
    blockers?: unknown;
    reason?: unknown;
  };
  const codeOf = "string" === typeof code ? code : "string" === typeof message ? message : "";
  const reasonOf = "string" === typeof reason ? reason : null;
  switch (Number(status)) {
    case 409:
      if ("CAPITAL_CHANGE_REFUSED" === codeOf) {
        return {
          kind: "REFUSED",
          blockers: Array.isArray(blockers)
            ? blockers.filter((b): b is string => "string" === typeof b)
            : [],
        };
      }
      return { kind: "UNKNOWN" };
    case 422:
      if ("CAPITAL_NOT_INSURABLE" === codeOf) {
        return { kind: "NOT_INSURABLE", reason: reasonOf };
      }
      return { kind: "INVALID_CAPITAL", reason: reasonOf };
    case 404:
      if ("CAPITAL_CHANGE_NOT_ENABLED" === codeOf) {
        return { kind: "NOT_ENABLED" };
      }
      return { kind: "UNKNOWN" };
    default:
      return { kind: "UNKNOWN" };
  }
};

/** `t(clave, texto italiano)`: la clave de getlife/translations o el texto si falta. */
export type CapitalChangeTranslateT = (key: string, fallback: string) => string;

/** Una frase por bloqueo, para el cliente. Un código nuevo cae en la genérica. */
export const capitalChangeBlockerText = (blocker: string, t: CapitalChangeTranslateT): string => {
  const prefix = "newDashboard.quotes.capitalChange.blocker";
  switch (blocker) {
    case "ARCHIVED":
      return t(`${prefix}.archived`, "Questo preventivo è archiviato.");
    case "PORTFOLIO_POLICY":
      return t(
        `${prefix}.portfolioPolicy`,
        "Il capitale di questa polizza non si può modificare da qui. Contatta il servizio clienti."
      );
    case "ADVISOR_LEAD":
      return t(
        `${prefix}.advisorLead`,
        "Questo preventivo lo segue il tuo consulente: chiedi a lui di modificare il capitale."
      );
    case "NO_QUOTE":
      return t(
        `${prefix}.noQuote`,
        "Questo preventivo non ha ancora un capitale da modificare: continua il preventivo."
      );
    case "NO_OFFER":
      return t(
        `${prefix}.noOffer`,
        "Scegli prima un'offerta: potrai modificare il capitale una volta scelta."
      );
    case "LEGAL_NOTE_ACCEPTED":
      return t(
        `${prefix}.legalNoteAccepted`,
        "Hai già accettato la nota legale: il capitale non si può più modificare da qui. Contatta il servizio clienti."
      );
    case "CONTRACT_GENERATED":
      return t(
        `${prefix}.contractGenerated`,
        "Il contratto è già stato generato: il capitale non si può più modificare da qui. Contatta il servizio clienti."
      );
    case "MEDICAL_EXPIRED":
      return t(
        `${prefix}.medicalExpired`,
        "Il questionario medico è scaduto: per un capitale diverso crea un nuovo preventivo."
      );
    case "MEDICAL_REJECTED":
      return t(
        `${prefix}.medicalRejected`,
        "Con le risposte al questionario medico non possiamo offrire questa copertura."
      );
    default:
      return t(`${prefix}.generic`, "Il capitale di questo preventivo non si può modificare.");
  }
};

/** Frase para el cliente de un error que no es de bloqueo. */
export const capitalChangeErrorText = (
  error: CapitalChangeErrorT,
  limits: CapitalChangeLimitsI,
  t: CapitalChangeTranslateT,
  money: (amount: number) => string,
  stage: "preview" | "confirm"
): string => {
  const prefix = "newDashboard.quotes.capitalChange.error";
  switch (error.kind) {
    case "INVALID_CAPITAL":
      return t(
        `${prefix}.invalidCapital`,
        "Scegli un capitale tra {{min}} e {{max}}, a multipli di {{step}}."
      )
        .replace("{{min}}", money(limits.min))
        .replace("{{max}}", money(limits.max))
        .replace("{{step}}", money(limits.step));
    case "NOT_INSURABLE":
      return t(
        `${prefix}.notInsurable`,
        "Questo capitale non è disponibile per la tua età. Prova con un altro importo."
      );
    case "NOT_ENABLED":
      return t(
        `${prefix}.notEnabled`,
        "La modifica del capitale non è disponibile al momento."
      );
    case "REFUSED":
      return 0 < error.blockers.length
        ? error.blockers.map((b) => capitalChangeBlockerText(b, t)).join(" ")
        : capitalChangeBlockerText("", t);
    default:
      return "preview" === stage
        ? t(
            `${prefix}.preview`,
            "Non siamo riusciti a calcolare il premio. Riprova tra qualche istante."
          )
        : t(
            `${prefix}.confirm`,
            "Non siamo riusciti a modificare il capitale. Riprova tra qualche istante."
          );
  }
};
